import React, { HTMLAttributes, Ref, useCallback } from 'react';
import '../../base.scss';
import './TextArea.scss';
import { classNames } from '../../utilities';

export interface TextAreaProps extends HTMLAttributes<HTMLTextAreaElement> {
  /** required for accessibility if no labels */
  'aria-label'?: string;
  /** Attach a custom class to the input container */
  className?: string;
  /** A ref to access the component */
  componentRef?: Ref<HTMLTextAreaElement>;
  /** Initial value for uncontrolled inputs */
  defaultValue?: string;
  /** Disable the input */
  disabled?: boolean;
  /** ID for the checkbox */
  id?: string;
  /** Maximum number of characters */
  maxLength?: number;
  /** Placeholder for empty input */
  placeholder?: string;
  /**
   * Input is not editable
   * @default false
   */
  readOnly?: boolean;
  /** Allow textarea to be resized */
  resizable?: boolean;
  /** Number of textarea rows */
  rows?: number;
  /** Value for controlled inputs */
  value?: string;
  /** Callback handler for value changes */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>, newValue?: string) => void;
}

export function TextArea(props: TextAreaProps) {
  const {
    className,
    defaultValue,
    disabled,
    id,
    maxLength,
    placeholder,
    readOnly,
    resizable,
    rows,
    value,
    onChange,
  } = props;

  if (defaultValue && value) {
    throw new Error('TextAreas should either be controlled (value) or uncontrolled (defaultValue).');
  }

  const inputClassNames = classNames('ps-textarea', resizable ? 'ps-textarea-non-resize' : null, className);

  const onChangeWrapper = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newVal = event.target.value ?? '';
      if (onChange) {
        onChange(event, newVal);
      }
    },
    [onChange]
  );

  return (
    <textarea
      aria-label={props['aria-label']}
      className={inputClassNames}
      defaultValue={defaultValue}
      disabled={disabled}
      id={id}
      maxLength={maxLength}
      placeholder={placeholder}
      readOnly={readOnly}
      rows={rows}
      value={value}
      onChange={onChangeWrapper}
    ></textarea>
  );
}
